import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import {
  deleteAppointment,
  getAppointmentDetails,
  getServiceSlots,
  updateAppointment,
} from "../../../services/appointments";
import CustomCalendar from "../../../components/Calendar/CustomCalendar";
import "../common.css";
import "./appointment-details.css";
import { useAuth } from "../../../context/AuthContext";
import LoginModal from "../../../components/Modals/LoginModal/LoginModal";

export default function AppointmentDetailsPage() {
  const navigate = useNavigate();
  const { appointmentID } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [appointmentData, setAppointmentData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const { token, setInstLogoData } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await getAppointmentDetails(appointmentID);
        setAppointmentData(res.data);
        setInstLogoData(res.data.institution.image);
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [appointmentID]);

  const editClicked = async () => {
    if (!token) {
      setShowLoginModal(true);
      return;
    }
    try {
      const res = await getServiceSlots(
        appointmentData.service?.id,
        appointmentData.branch?.id
      );
      setSlots(res.data);
      setIsEditing(true);
    } catch (err) {
      console.log(err);
    }
  };

  const handleUpdateAppointment = async () => {
    if (!selectedSlot || !selectedDate || !token) return;
    try {
      await updateAppointment(token, appointmentID, {
        appointmentDate: selectedDate,
        startTime: selectedSlot,
      });
      window.location.reload();
    } catch (err) {
      console.log(err);
    }
  };

  const deleteClicked = async () => {
    if (!token) {
      setShowLoginModal(true);
      return;
    }
    try {
      await deleteAppointment(token, appointmentID);
      alert("تم حذف الموعد بنجاح");
      navigate(`/${appointmentData.institution?.id}`);
    } catch (err) {
      console.log(err);
    }
  };

  const formatDate = (dateStr, includeTime = true) => {
    if (!dateStr) return "غير متوفر";
    return new Date(dateStr).toLocaleDateString("ar-EG", {
      year: "numeric",
      month: "long",
      day: "numeric",
      ...(includeTime && { hour: "2-digit", minute: "2-digit" }),
    });
  };

  const renderStatusTag = (status) => {
    const statusMap = {
      pending: "قيد الانتظار",
      approved: "تمت الموافقة",
      cancelled: "ملغى",
      completed: "تم",
    };
    const label = statusMap[status] || "غير معروف";
    const className = `status-tag status-${status || "unknown"}`;
    return <span className={className}>{label}</span>;
  };

  return (
    <div className="page center">
      <style>{`
        .center {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .appointment-summary-box {
          background: #fff;
          border-radius: 16px;
          border: 1px solid var(--clr-border);
          box-shadow: 0 2px 6px rgba(0,0,0,0.04);
          width: 100%;
          max-width: 500px;
          padding: 24px;
          margin: auto;
          direction: rtl;
          font-family: 'Tajawal', sans-serif;
          text-align: center;
        }
        .details-card {
          background: #f9f9f9;
          border-radius: 12px;
          padding: 16px;
          font-size: 15px;
          color: var(--clr-text-main);
          text-align: right;
          line-height: 2;
          margin-bottom: 20px;
        }
        .slots-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 10px;
        }
        .slot-button {
          padding: 8px 12px;
          border-radius: 6px;
          background-color: #f0f0f0;
          border: none;
          cursor: pointer;
        }
        .slot-button.selected {
          background-color: var(--clr-primary);
          color: white;
          font-weight: bold;
        }
      `}</style>

      <div className="content-view center">
        {isLoading ? (
          <CircularProgress />
        ) : (
          <div className="appointment-summary-box">
            <h2 className="section-title">🗓️ تفاصيل الموعد</h2>
            <div className="appointment-id">
              #{appointmentData?.turnNumber || appointmentData?.id}
            </div>

            <div className="status-tag-wrapper">
              {renderStatusTag(appointmentData.status)}
            </div>

            <div className="details-card">
              <div>🏢 <strong>المؤسسة:</strong> {appointmentData?.institution?.name}</div>
              <div>📍 <strong>الفرع:</strong> {appointmentData?.branch?.name}</div>
              <div>🛠️ <strong>الخدمة:</strong> {appointmentData?.service?.name}</div>
              <div>🗓️ <strong>الموعد:</strong> {formatDate(appointmentData?.date)}</div>
              {appointmentData.startTime && appointmentData.endTime && (
                <div>⏰ <strong>الوقت:</strong> من {appointmentData.startTime} إلى {appointmentData.endTime}</div>
              )}
            </div>

            {isEditing && (
              <div className="calendar-slots-container" style={{ marginTop: 20 }}>
                <CustomCalendar
                  value={selectedDate ? new Date(selectedDate) : null}
                  onChange={(date) => {
                    const localISO = new Date(
                      date.getTime() - date.getTimezoneOffset() * 60000
                    ).toISOString().split("T")[0];
                    setSelectedDate(localISO);
                  }}
                  tileDisabled={({ date }) =>
                    !slots.some(
                      (s) =>
                        s.date ===
                        new Date(
                          date.getTime() - date.getTimezoneOffset() * 60000
                        ).toISOString().split("T")[0]
                    )
                  }
                />

                {selectedDate &&
                slots.find((s) => s.date === selectedDate) ? (
                  <div className="slots-grid">
                    {slots
                      .find((s) => s.date === selectedDate)
                      .slots.map((slot, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedSlot(slot.start)}
                          className={`slot-button ${
                            selectedSlot === slot.start ? "selected" : ""
                          }`}
                        >
                          {slot.start} - {slot.end}
                        </button>
                      ))}
                  </div>
                ) : (
                  <p style={{ color: "#888", marginTop: 10 }}>
                    اختر يومًا لعرض المواعيد المتاحة
                  </p>
                )}

                <div style={{ display: "flex", gap: 10, marginTop: 15 }}>
                  <button
                    onClick={handleUpdateAppointment}
                    disabled={!selectedSlot}
                    className="btn outline"
                    style={{ backgroundColor: "#01c8aa", color: "white" }}
                  >
                    تأكيد التعديل
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="btn outline"
                  >
                    إلغاء
                  </button>
                </div>
              </div>
            )}

            {!isEditing && (
              <div className="actions-row" style={{ marginTop: 30 }}>
                <button className="btn outline demo" onClick={editClicked}>
                  تعديل
                </button>
                <button className="btn outline demo-err" onClick={deleteClicked}>
                  حذف
                </button>
              </div>
            )}
          </div>
        )}

        {showLoginModal && (
          <LoginModal
            onClose={() => setShowLoginModal(false)}
            onLoginSuccess={() => editClicked}
          />
        )}
      </div>
    </div>
  );
}
